import { useEffect, useState } from 'react';
import {
  generatePath,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  PermissionEnum,
  ButtonSecondary,
  ButtonSize,
  PermissionDenied,
  IconLoader,
  IconDownload,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  ButtonPrimary,
  IconFile,
  Select,
  Autocomplete,
  responseErrorsResolver,
  ResponseError,
  SignaturePopup,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useImportIncomingTransactionsMutation,
  useCreateImportSignatureMutation,
} from '../transactions/store/services';
import DocumentUpload from './upload';
import ImportTransactionList from './list';
import useGetPrimaryAccountsAutocompleteList from '../../store/dictionaries/helpers/use-primary-accounts-autocomplete';
import useGetPspPartnersAutocompleteList from '../../store/dictionaries/helpers/use-psp-partners-autocomplete';
import { clearImportTransactionListState } from './store/slices/list';
import {
  useSendCodeMutation,
  useValidateCodeMutation,
} from '../../store/signature/service';
import { MfaToken, MfaType } from '../../store/types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

let timer: NodeJS.Timeout;

const ImportPage = (): React.JSX.Element => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [expand, setExpand] = useState(false);
  const [pspPartnerId, setPspPartnerId] = useState('');
  const [pspPartnerAccountId, setPspPartnerAccountId] = useState('');
  const [orderUploadId, setOrderUploadId] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [signaturePopupVisible, setSignaturePopupVisible] = useState(false);
  const [errorMessageSignature, setErrorMessageSignature] = useState('');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const [importTransactions, { isLoading: requestInProgress, isSuccess }] =
    useImportIncomingTransactionsMutation();

  const [sendCode, { isLoading: sendCodeInProgress }] = useSendCodeMutation();
  const [validateCode, { isLoading: validateCodeInProgress }] =
    useValidateCodeMutation();
  const [
    createSignature,
    {
      isLoading: createSignatureInProgress,
      isSuccess: createSignatureIsSuccess,
    },
  ] = useCreateImportSignatureMutation();

  const { setError } = useForm();

  const {
    transactionsDictionaries: { activePermissions },
    signatures: { mfaToken },
  } = useAppSelector((state) => state);

  const { autocompleteList: pspPartners } = useGetPspPartnersAutocompleteList();

  const {
    autocompleteList: pspPartnerAccounts,
    isLoading: pspPartnerAccountsIsLoading,
    onScroll,
    nextPageIsLoading,
    pagination,
    onSearch,
  } = useGetPrimaryAccountsAutocompleteList(pspPartnerId, '', true);

  const onSendCode = async () => {
    clearTimeout(timer);

    const response = (await sendCode({ mfa: MfaType.TOTP })) as {
      data: { data: MfaToken };
    };

    if (response && response.data && response.data.data) {
      setSignaturePopupVisible(true);

      const expireTime = Math.round(
        // eslint-disable-next-line react-hooks/purity
        new Date(response.data.data.expiresAt).getTime() - Date.now(),
      );

      timer = setTimeout(() => {
        onSendCode();
      }, expireTime);
    }
  };

  const onImportTransactions = async () => {
    const response = (await importTransactions({
      orderUploadId,
      pspPartnerId,
      pspPartnerAccountId,
    })) as { error?: ResponseError; data?: { data?: { id: string } } };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    } else {
      setSelectedItemId(response?.data?.data?.id as string);
      onSendCode();
    }
  };

  const validateCodeAction = async (code: string) => {
    clearTimeout(timer);
    setErrorMessageSignature('');

    if (code) {
      const response = (await validateCode({
        mfaToken: mfaToken as string,
        totpToken: code,
      })) as {
        data?: { data: MfaToken };
        error: { data: { message: string; statusCode: number } };
      };

      if (response && response.data && response.data.data) {
        const createResponse = (await createSignature({
          id: selectedItemId,
          body: {
            mfaToken: response.data.data.token,
            signedAt: new Date().toISOString(),
          },
        })) as { error: { data: { message: string }; status: number } };

        if (
          createResponse &&
          createResponse.error &&
          createResponse.error.data
        ) {
          if (createResponse.error?.status === 403) {
            setErrorMessageSignature('Access denied');
          } else {
            responseErrorsResolver({
              errors: createResponse.error as ResponseError,
              setError: () => {},
              setGlobalError: setErrorMessageSignature,
            });
          }
        } else {
          setSignaturePopupVisible(false);
          setErrorMessageSignature('');
        }
      } else if (response && response.error && response.error.data) {
        if (response.error.data.statusCode === 403) {
          setErrorMessageSignature('Access denied');
        } else if (response.error.data.statusCode === 401) {
          setErrorMessageSignature('Incorrect OTP code');
        } else {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError: () => {},
            setGlobalError: setErrorMessageSignature,
          });
        }
      }
    } else {
      setErrorMessageSignature('OTP Code should not be empty');
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    setFileName('');
    setGlobalError('');
    setSelectedItemId('');
    setPspPartnerAccountId('');
    setPspPartnerId('');
  };

  const onSignatureCancelClick = () => {
    clearTimeout(timer);
    setSignaturePopupVisible(false);

    navigate(
      `${generatePath(ROUTES.IMPORT_ITEM, {
        transactionId: selectedItemId,
      })}${search || ''}`,
    );

    handleClose();
  };

  useEffect(() => {
    if (createSignatureIsSuccess && selectedItemId) {
      navigate(
        `${generatePath(ROUTES.IMPORT_ITEM, {
          transactionId: selectedItemId,
        })}${search || ''}`,
      );

      handleClose();
    }
  }, [createSignatureIsSuccess, selectedItemId]);

  useEffect(
    () => () => {
      dispatch(clearImportTransactionListState());
    },
    [dispatch],
  );

  useEffect(() => {
    let title = 'Import transactions';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let globalContent;

  if (!activePermissions) {
    globalContent = (
      <div className={cn.importLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    activePermissions &&
    activePermissions.includes(PermissionEnum.TRANSACTIONS_IMPORT_LIST)
  ) {
    globalContent = (
      <div className={cn.import}>
        <div className={cn.importTitle}>
          Import transactions
          <ButtonSecondary
            icon={<IconDownload />}
            text="Download template"
            size={ButtonSize.SMALL}
            className="button-white"
            onClick={() =>
              window.open(
                'https://baseella-static.s3.us-east-2.amazonaws.com/transactions-import-templates/Baseella+incoming+transactions+import+template+.xlsx',
              )
            }
          />
        </div>
        <div className={cn.import}>
          <DocumentUpload
            setPanelIsOpen={setPanelIsOpen}
            setFileName={setFileName}
            setOrderUploadId={setOrderUploadId}
          />
          <ImportTransactionList
            isSuccess={isSuccess || createSignatureIsSuccess}
          />
        </div>
        <SidePanel
          type={SidePanelType.Accounts}
          closeOnClickOutside
          isOpen={panelIsOpen}
          onClose={handleClose}
          title="Incoming transaction"
          errorSidepanel={globalError}
          sidepanelExpandedKey="import-transaction-panel"
          footer={
            <>
              <ButtonPrimary
                text={
                  requestInProgress || sendCodeInProgress
                    ? 'Loading...'
                    : 'Add transactions'
                }
                className={cn.markReadButton}
                onClick={onImportTransactions}
                disabled={
                  !pspPartnerAccountId || !pspPartnerId || requestInProgress
                }
                bold
              />

              <ButtonPseudo text="Close" onClick={handleClose} bold />
            </>
          }
          onExpandClick={() => setExpand(!expand)}
        >
          <div>
            <div className={cn.uploadedFile}>
              <IconFile />
              {fileName}
            </div>
            <div className={cn.detailsTitle}>Details</div>
            <div className={cn.formGroup}>
              <Select
                name="pspPartnerId"
                value={pspPartnerId}
                title="Partner Bank"
                list={pspPartners}
                onChange={(e) => setPspPartnerId(e.target.value as string)}
                placeholder="Select partner bank"
                fullWidth
              />
            </div>
            <div className={cn.formGroup}>
              <Autocomplete
                name="pspPartnerAccountId"
                value={pspPartnerAccountId}
                title="Partner Bank Account"
                list={pspPartnerAccounts}
                onChange={(e) =>
                  setPspPartnerAccountId(e.target.value as string)
                }
                placeholder="Select partner bank account"
                disabled={!pspPartnerId}
                isLoading={pspPartnerAccountsIsLoading}
                onInfinateScroll={onScroll}
                nextPageIsLoading={nextPageIsLoading}
                hasNextPage={pagination?.hasNextPage}
                onSearch={onSearch}
                withInfiniteScroll
                withSuffix
                fullWidth
              />
            </div>
            <div className={cn.separator} />
          </div>
        </SidePanel>
        <SignaturePopup
          signaturePopupVisible={signaturePopupVisible}
          validateCode={validateCodeAction}
          validateCodeInProgress={
            validateCodeInProgress || createSignatureInProgress
          }
          errorMessageSignature={errorMessageSignature}
          onSignatureCancelClick={onSignatureCancelClick}
        />
        <Outlet />
        <div id="signature-popup" />
      </div>
    );
  } else {
    globalContent = <PermissionDenied />;
  }

  return globalContent;
};

export default ImportPage;
